import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo from "@/components/feature/Seo";
import MediaCard from "@/components/feature/MediaCard";
import MediaGrid from "@/components/feature/MediaGrid";
import { mediaList, searchMedia, typeLabel } from "@/mocks/media";

const hotKeywords = ["悬疑", "治愈", "科幻", "都市", "美食", "青春", "国产", "综艺"];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setInput(keyword);
    if (!keyword) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 350);
    return () => window.clearTimeout(timer);
  }, [keyword]);

  const results = useMemo(() => searchMedia(keyword), [keyword]);
  const hotList = useMemo(() => [...mediaList].sort((a, b) => b.views - a.views).slice(0, 4), []);
  const hasKeyword = keyword.trim().length > 0;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const q = input.trim();
    setParams(q ? { q } : {}, { replace: true });
  };

  const quickSearch = (word: string) => {
    setParams({ q: word });
  };

  return (
    <SiteLayout active="search">
      <Seo
        title={hasKeyword ? `搜索“${keyword}” - 91视频网站` : "影视搜索 - 91视频网站"}
        description={
          hasKeyword
            ? `91视频网站搜索“${keyword}”的结果，共找到 ${results.length} 部相关影视作品，包含电影、电视剧与综艺。`
            : "在 91视频网站搜索电影、电视剧与综艺，支持按片名、类型、地区关键词快速查找影视作品。"
        }
        keywords="91视频网站,影视搜索,电影搜索,电视剧搜索,综艺搜索"
      />

      <div className="w-full bg-primary-900">
        <div className="w-full px-4 md:px-6 xl:px-10 py-8 md:py-10">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-background-300 mb-4">
            <Link to="/" className="hover:text-secondary-300 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-secondary-400">搜索</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50">影视搜索</h1>
          <p className="mt-2 text-sm text-background-300">输入片名、类型或地区，快速定位想看的内容</p>

          <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-2 max-w-2xl">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="例如：悬疑、深海信号、综艺"
                aria-label="搜索影视作品"
                className="w-full h-11 pl-11 pr-4 text-sm rounded-md bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="h-11 px-6 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-background-400">热门搜索</span>
            {hotKeywords.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => quickSearch(word)}
                className="px-2.5 h-7 rounded-md bg-primary-800 text-background-200 text-xs hover:bg-secondary-500 hover:text-foreground-950 transition-colors whitespace-nowrap"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 xl:px-10 py-7 md:py-9">
        {hasKeyword ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <p className="text-sm text-foreground-600">
                关键词 <span className="text-foreground-950 font-medium">“{keyword}”</span> 共找到{" "}
                <span className="text-accent-600 font-semibold">{results.length}</span> 个相关结果
              </p>
              <button
                type="button"
                onClick={() => { setParams({}); setInput(""); }}
                className="text-xs text-foreground-500 hover:text-primary-600 inline-flex items-center gap-1"
              >
                <i className="ri-close-circle-line"></i>
                清除关键词
              </button>
            </div>

            {loading ? (
              <MediaGrid items={[]} loading />
            ) : (
              <MediaGrid
                items={results}
                emptyTitle="没有找到相关影视"
                emptyDesc={`没有与“${keyword}”匹配的作品，换一个关键词试试，比如“悬疑”“治愈”或者直接浏览热门推荐。`}
              />
            )}

            {!loading && results.length > 0 && (
              <p className="mt-6 text-xs text-foreground-400">
                提示：搜索结果同时匹配片名、类型、地区与简介内容。
              </p>
            )}
          </>
        ) : (
          <section aria-label="热门影视入口">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500"></span>
              热门影视入口
            </h2>
            <p className="text-sm text-foreground-500 mt-1.5">还没有输入关键词？先从这些热门作品开始浏览</p>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {hotList.map((item) => (
                <MediaCard key={item.id} media={item} />
              ))}
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-2">
              {(["movie", "tv", "variety"] as const).map((t) => (
                <Link
                  key={t}
                  to={`/category/${t}`}
                  className="h-9 px-4 inline-flex items-center rounded-md border border-background-300 text-foreground-700 text-sm hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  浏览{typeLabel[t]}频道
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </SiteLayout>
  );
}